import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  CircularProgress,
  Alert,
  Paper,
} from '@mui/material';
import { Storage, CheckCircle, Warning, ContentCopy } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { CREATE_MEDIA_BUCKET_SQL } from '../../../../utils/checkStorage';

interface StorageCheckDialogProps {
  open: boolean;
  onClose: () => void;
  checking: boolean;
  result: {
    success?: boolean;
    error?: string;
    message?: string;
  } | null;
  onCheck: () => void;
  onCopySQL: () => void;
}

const StorageCheckDialog: React.FC<StorageCheckDialogProps> = ({
  open,
  onClose,
  checking,
  result,
  onCheck,
  onCopySQL,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Storage />
          {t("admin.contentManagement.storage.title")}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ py: 2 }}>
          {checking ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>
                {t("admin.contentManagement.storage.checking")}
              </Typography>
            </Box>
          ) : result ? (
            <Box>
              {result.success ? (
                <Alert
                  severity="success"
                  icon={<CheckCircle />}
                  sx={{ mb: 2 }}
                >
                  {result.message}
                </Alert>
              ) : (
                <>
                  <Alert severity="error" icon={<Warning />} sx={{ mb: 2 }}>
                    {result.error}
                  </Alert>
                  <Paper sx={{ p: 2, bgcolor: "grey.100", mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {t("admin.contentManagement.storage.fixIntro")}
                    </Typography>
                    <Box
                      sx={{
                        mt: 1,
                        p: 2,
                        bgcolor: "grey.900",
                        borderRadius: 1,
                        fontFamily: "monospace",
                        fontSize: "0.875rem",
                        color: "common.white",
                        overflow: "auto",
                        maxHeight: 300,
                      }}
                    >
                      <pre style={{ margin: 0 }}>
                        {CREATE_MEDIA_BUCKET_SQL}
                      </pre>
                    </Box>
                    <Button
                      startIcon={<ContentCopy />}
                      onClick={onCopySQL}
                      sx={{ mt: 1 }}
                    >
                      {t("admin.contentManagement.storage.copySql")}
                    </Button>
                  </Paper>
                  <Box
                    component="ol"
                    sx={{
                      m: 0,
                      pl: 3,
                      color: "text.secondary",
                      typography: "body2",
                    }}
                  >
                    <li>{t("admin.contentManagement.storage.steps.goToDashboard")}</li>
                    <li>{t("admin.contentManagement.storage.steps.openSqlEditor")}</li>
                    <li>{t("admin.contentManagement.storage.steps.pasteAndRun")}</li>
                    <li>{t("admin.contentManagement.storage.steps.recheck")}</li>
                  </Box>
                </>
              )}
            </Box>
          ) : (
            <Typography color="text.secondary">
              {t("admin.contentManagement.storage.clickToCheck")}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {t("admin.contentManagement.storage.close")}
        </Button>
        <Button
          variant="contained"
          onClick={onCheck}
          disabled={checking}
          sx={{
            background: "linear-gradient(135deg, #00d4ff 0%, #0099cc 100%)",
          }}
        >
          {t("admin.contentManagement.storage.checkStorage")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StorageCheckDialog;